import { BLOG_POSTS } from "./blog-posts";
import type { BlogPost } from "./blog-types";

export type BlogCategory = {
  name: string;
  slug: string;
  blurb: string;
  popular?: boolean;
};

export const BLOG_CATEGORIES: BlogCategory[] = [
  { name: "Back Pain", slug: "back-pain", blurb: "Lower back pain causes, exercises and when to get assessed.", popular: true },
  { name: "Neck Pain", slug: "neck-pain", blurb: "Stiff necks, desk related pain and gentle mobility work.", popular: true },
  { name: "Shoulder Pain", slug: "shoulder-pain", blurb: "Impingement, rotator cuff pain and shoulder rehab.", popular: true },
  { name: "Knee Pain", slug: "knee-pain", blurb: "Runner's knee, joint pain and strengthening routines.", popular: true },
  { name: "Hip Pain", slug: "hip-pain", blurb: "Hip and glute pain, mobility and post surgical recovery." },
  { name: "Sciatica", slug: "sciatica", blurb: "Nerve related leg pain, safe stretches and red flags.", popular: true },
  { name: "Sports", slug: "sports", blurb: "Sports injuries, return to play and performance.", popular: true },
  { name: "Running Injuries", slug: "running-injuries", blurb: "Training load, common running injuries and prevention." },
  { name: "Posture", slug: "posture", blurb: "Posture habits, desk setup and forward head position." },
  { name: "Muscle Pain", slug: "muscle-pain", blurb: "Tight muscles, spasm, cramps and recovery." },
  { name: "Joint Pain", slug: "joint-pain", blurb: "Arthritic and general joint pain management." },
  { name: "Rehab", slug: "rehab", blurb: "Post operative and neurological rehabilitation." },
  { name: "Physiotherapy Exercises", slug: "physiotherapy-exercises", blurb: "Guided exercise programmes you can start at home." },
  { name: "Injury Prevention", slug: "injury-prevention", blurb: "Reduce your risk before pain becomes a problem." },
  { name: "Workplace Ergonomics", slug: "workplace-ergonomics", blurb: "Desk, chair and screen setup for pain free work." },
  { name: "Home Physio", slug: "home-physio", blurb: "Physiotherapy in your own home across Centurion and Midrand." },
  { name: "General", slug: "general", blurb: "Practical guidance on physiotherapy, medical aid and appointments." },
];

export const CATEGORY_BY_SLUG: Record<string, BlogCategory> = Object.fromEntries(
  BLOG_CATEGORIES.map((c) => [c.slug, c]),
);

export function categorySlug(name: string): string {
  const found = BLOG_CATEGORIES.find((c) => c.name.toLowerCase() === name.toLowerCase());
  if (found) return found.slug;
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export function postsInCategory(slug: string): BlogPost[] {
  return BLOG_POSTS.filter((p) => categorySlug(p.category) === slug).sort(
    (a, b) => (b.publishedAt > a.publishedAt ? 1 : -1),
  );
}

export function categoryCounts(): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of BLOG_POSTS) {
    const s = categorySlug(p.category);
    counts[s] = (counts[s] ?? 0) + 1;
  }
  return counts;
}

export const FEATURED_POSTS: BlogPost[] = BLOG_POSTS.filter((p) => p.featured || p.isPillar).slice(0, 6);

/** Simple keyword search across title, intro, category, tags, exercises and FAQs. */
export function searchPosts(query: string): BlogPost[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);
  const scored = BLOG_POSTS.map((p) => {
    const haystack = [
      p.title,
      p.metaDescription,
      p.intro,
      p.category,
      ...(p.tags ?? []),
      ...(p.exercises ?? []).map((e) => e.name),
      ...p.faqs.map((f) => f.q),
      ...p.sections.map((s) => s.heading),
    ]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (const t of terms) {
      if (!haystack.includes(t)) return { p, score: 0 };
      score += p.title.toLowerCase().includes(t) ? 3 : 1;
    }
    return { p, score };
  })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored.map((x) => x.p);
}

/** Related articles: explicit list first, then same category, then pillar. */
export function relatedPostsFor(post: BlogPost, limit = 4): BlogPost[] {
  const bySlug = new Map(BLOG_POSTS.map((p) => [p.slug, p]));
  const out: BlogPost[] = [];
  const push = (p?: BlogPost) => {
    if (p && p.slug !== post.slug && !out.some((x) => x.slug === p.slug)) out.push(p);
  };
  for (const s of post.relatedPosts ?? []) push(bySlug.get(s));
  if (post.pillarSlug) push(bySlug.get(post.pillarSlug));
  for (const p of BLOG_POSTS) {
    if (out.length >= limit) break;
    if (p.category === post.category) push(p);
  }
  return out.slice(0, limit);
}
